import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  clearCachedUserAccess,
  getCachedUserAccessSnapshot,
  getUserAccess,
} from "../utils/authRoles";
import { traceAsync } from "../utils/perfTrace";

export default function BrokerRoute({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const pendingAttemptsRef = useRef(0);
  const redirect = `${location.pathname}${location.search}`;
  const loginPath = `/login?redirect=${encodeURIComponent(redirect)}`;
  const [state, setState] = useState(() => {
    const cachedAccess = getCachedUserAccessSnapshot();
    return {
      loading: !cachedAccess,
      signedIn: Boolean(cachedAccess?.isBroker || cachedAccess?.isAdmin),
      access: cachedAccess,
    };
  });

  useEffect(() => {
    let mounted = true;
    let checkId = 0;
    let retryTimer = null;

    const checkAccess = async () => {
      const currentCheck = ++checkId;
      const access = await traceAsync("route.broker.access", () => getUserAccess(), {
        attempt: pendingAttemptsRef.current + 1,
      });
      if (!mounted || currentCheck !== checkId) return;

      if (access.accessPending) {
        pendingAttemptsRef.current += 1;
        if (pendingAttemptsRef.current >= 8) {
          clearCachedUserAccess();
          setState({ loading: false, signedIn: false, access: null });
          navigate(loginPath, { replace: true });
          return;
        }

        setState((previous) => {
          if (previous.signedIn && previous.access) return previous;
          return { loading: true, signedIn: true, access: null };
        });
        if (retryTimer) clearTimeout(retryTimer);
        retryTimer = setTimeout(checkAccess, 1000);
        return;
      }

      pendingAttemptsRef.current = 0;
      setState({
        loading: false,
        signedIn: !!access.user,
        access,
      });

      if (!access.user) {
        navigate(loginPath, { replace: true });
      }
    };

    checkAccess();

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (event === "SIGNED_OUT") {
        checkId += 1;
        clearCachedUserAccess();
        setState({ loading: false, signedIn: false, access: null });
        navigate(loginPath, { replace: true });
        return;
      }

      if (!session) {
        return;
      }

      await checkAccess();
    });

    return () => {
      mounted = false;
      if (retryTimer) clearTimeout(retryTimer);
      authListener?.subscription?.unsubscribe?.();
    };
  }, [loginPath, navigate]);

  if (state.loading) {
    return null;
  }

  if (!state.signedIn) {
    return <Navigate to={loginPath} replace />;
  }

  if (state.access?.isAdmin && !state.access?.isBroker) {
    return <Navigate to="/admin" replace />;
  }

  return children;
}
